import {useMemo, useState} from 'react';
import type {ProductCatalogItem} from '@ozon-ai/ozon/contracts';
import {
	Combobox,
	ComboboxContent,
	ComboboxInput,
	ComboboxItem,
	ComboboxList,
} from '@/components/ui/combobox.tsx';
import {Label} from '@/components/ui/label.tsx';
import {searchCatalog} from './offerCatalogSearch';
import {ProductCatalogComboboxItem} from './product-catalog-combobox-item';

export type ProductSkuComboboxValueKey = 'ozonSku' | 'offerId';

export function getProductCatalogItemValue(
	item: ProductCatalogItem,
	valueKey: ProductSkuComboboxValueKey,
): string {
	if (valueKey === 'ozonSku') {
		return item.ozonSku ?? '';
	}
	return item.offerId;
}

export type ProductSkuComboboxProps = {
	id?: string;
	label?: string;
	catalog: ProductCatalogItem[];
	value: string;
	onValueChange: (value: string, item: ProductCatalogItem | null) => void;
	valueKey?: ProductSkuComboboxValueKey;
	disabled?: boolean;
	placeholder?: string;
	filterItem?: (item: ProductCatalogItem) => boolean;
};

export function ProductSkuCombobox({
	id,
	label,
	catalog,
	value,
	onValueChange,
	valueKey = 'ozonSku',
	disabled = false,
	placeholder = 'Поиск по артикулу, названию или SKU',
	filterItem,
}: ProductSkuComboboxProps) {
	const [open, setOpen] = useState(false);
	const [inputValue, setInputValue] = useState('');

	const items = useMemo(() => {
		return catalog
			.filter((item) => (filterItem ? filterItem(item) : true))
			.filter((item) => getProductCatalogItemValue(item, valueKey));
	}, [catalog, filterItem, valueKey]);

	const filtered = useMemo(() => searchCatalog(items, inputValue), [items, inputValue]);

	const filteredValues = useMemo(
		() => filtered.map((item) => getProductCatalogItemValue(item, valueKey)),
		[filtered, valueKey],
	);

	const itemByValue = useMemo(() => {
		const map = new Map<string, ProductCatalogItem>();
		for (const item of items) {
			map.set(getProductCatalogItemValue(item, valueKey), item);
		}
		return map;
	}, [items, valueKey]);

	function resolveLabel(itemValue: string | null): string {
		if (!itemValue) return '';
		return itemByValue.get(itemValue)?.name ?? itemValue;
	}

	function handleOpenChange(nextOpen: boolean) {
		setOpen(nextOpen);
		if (nextOpen) {
			setInputValue('');
			return;
		}
		setInputValue(resolveLabel(value || null));
	}

	function handleInputValueChange(next: string) {
		setInputValue(next);
		setOpen(true);
	}

	function handleSelect(nextValue: string | null) {
		if (!nextValue) {
			onValueChange('', null);
			setInputValue('');
			return;
		}
		const item = itemByValue.get(nextValue) ?? null;
		onValueChange(nextValue, item);
		setInputValue(resolveLabel(nextValue));
		setOpen(false);
	}

	const comboboxInputValue = open ? inputValue : resolveLabel(value || null);

	return (
		<div className="flex w-full flex-col gap-1.5">
			{label ? <Label htmlFor={id}>{label}</Label> : null}
			<Combobox
				open={open}
				onOpenChange={handleOpenChange}
				value={value || null}
				onValueChange={handleSelect}
				disabled={disabled}
				filter={null}
				filteredItems={filteredValues}
				inputValue={comboboxInputValue}
				onInputValueChange={handleInputValueChange}
				itemToStringLabel={resolveLabel}
			>
				<ComboboxInput
					id={id}
					className="w-full"
					disabled={disabled}
					placeholder={placeholder}
					showClear={Boolean(value)}
				/>
				<ComboboxContent className="w-[var(--anchor-width)] overflow-hidden p-0">
					<ComboboxList>
						{filtered.map((item) => {
							const itemValue = getProductCatalogItemValue(item, valueKey);
							return (
								<ComboboxItem key={item.offerId} value={itemValue} className="cursor-pointer">
									<ProductCatalogComboboxItem item={item} valueKey={valueKey}/>
								</ComboboxItem>
							);
						})}
					</ComboboxList>
					{filtered.length === 0 && (
						<p className="px-3 py-2 text-center text-sm text-muted-foreground">
							Ничего не найдено
						</p>
					)}
				</ComboboxContent>
			</Combobox>
		</div>
	);
}
